import { service } from '@ember/service';
import Route from '@ember/routing/route';
import type SessionContextService from 'tio-employee/services/session-context';
import type ApiService from 'tio-employee/services/api';
import type TuitionAssistanceService from 'tio-common/services/tuition-assistance';
import type { LimitsStatusDataSignature } from 'tio-common/services/tuition-assistance';
import { OpenSearchQueryBuilder } from 'tio-common/utils/open-search-builder';
import { OpenSearchResponseBuilder } from 'tio-common/utils/open-search-response-builder';
import type { OpenSearchResponse } from 'tio-common/models/search';
import {
  type RecentApplication,
  ALL_APPLICATION_STATES,
  ALL_PROGRAM_INSTANCE_STATES,
  buildType4Instances,
  normalizePaymentFields,
} from 'tio-common/utils/tuition-assistance/application-states';

export interface BenefitHistoryModel {
  applications: RecentApplication[];
  programInstances: Array<{
    instanceApplication: RecentApplication;
    childApplications: RecentApplication[];
  }>;
  availableYears: number[];
  currentYear: number;
  dependentsOnly: boolean;
  hasDependentPrograms: boolean;
  limitsStatusCurrent?: LimitsStatusDataSignature;
  limitsStatusPrevious?: LimitsStatusDataSignature;
  limitsStatusNext?: LimitsStatusDataSignature;
}

export default class AuthenticatedTasBenefitHistoryRoute extends Route {
  @service declare api: ApiService;
  @service declare sessionContext: SessionContextService;
  @service declare tuitionAssistance: TuitionAssistanceService;

  queryParams = {
    dependents: { refreshModel: true },
  };

  async model(params: { dependents?: string }): Promise<BenefitHistoryModel> {
    const dependentsOnly = params.dependents === 'true';
    const currentEmployee = this.sessionContext.currentEmployee;

    // Validate employee exists and has required relationships
    if (!currentEmployee?.id) {
      console.error('No current employee found in session context');
      throw new Error('Employee information not loaded. Please refresh the page.');
    }

    const companyId = currentEmployee.company?.id;
    if (!companyId) {
      console.error('Company ID not available on currentEmployee', {
        employeeId: currentEmployee.id,
        company: currentEmployee.company,
      });
      throw new Error('Company information not loaded. Please refresh the page.');
    }

    const hasDependentPrograms =
      currentEmployee.company?.companySetting?.tuitionAssistance?.isDependentProgramsEnabled ??
      false;

    const { flatApplications, programInstances, availableYears } = await this.fetchBenefitHistory(
      currentEmployee.id,
      companyId,
      { dependentOnly: dependentsOnly }
    );

    // Skip limits status fetch for dependent-only view (not relevant there)
    let limitsStatusCurrent: LimitsStatusDataSignature | undefined;
    let limitsStatusPrevious: LimitsStatusDataSignature | undefined;
    let limitsStatusNext: LimitsStatusDataSignature | undefined;

    if (!dependentsOnly) {
      limitsStatusCurrent = await this.tuitionAssistance.getV2LimitsStatus(currentEmployee);

      if (limitsStatusCurrent?.annual_start_date) {
        const currentStart = new Date(limitsStatusCurrent.annual_start_date);
        const previousDate = new Date(currentStart);
        previousDate.setFullYear(currentStart.getFullYear() - 1);

        const nextDate = new Date(currentStart);
        nextDate.setFullYear(currentStart.getFullYear() + 1);

        const previousDateParam = previousDate.toISOString().slice(0, 10);
        const nextDateParam = nextDate.toISOString().slice(0, 10);

        [limitsStatusPrevious, limitsStatusNext] = await Promise.all([
          this.tuitionAssistance.getV2LimitsStatus(currentEmployee, undefined, previousDateParam),
          this.tuitionAssistance.getV2LimitsStatus(currentEmployee, undefined, nextDateParam),
        ]);
      }
    }

    const currentYear = new Date().getFullYear();

    return {
      applications: flatApplications,
      programInstances,
      availableYears: availableYears.sort((a, b) => b - a), // Descending
      currentYear,
      dependentsOnly,
      hasDependentPrograms,
      limitsStatusCurrent,
      limitsStatusPrevious,
      limitsStatusNext,
    };
  }

  /**
   * Fetch all benefit history (no date restriction like dashboard)
   * Returns applications and instances grouped by year
   */
  async fetchBenefitHistory(
    employeeId: string,
    companyId: string,
    { dependentOnly = false }: { dependentOnly?: boolean } = {}
  ): Promise<{
    flatApplications: RecentApplication[];
    programInstances: Array<{
      instanceApplication: RecentApplication;
      childApplications: RecentApplication[];
    }>;
    availableYears: number[];
  }> {
    try {
      // Query ALL applications (no date restriction)
      const builder = new OpenSearchQueryBuilder()
        .mustTerm('company_id', companyId)
        .mustTerm('employee_id', employeeId);

      // When viewing dependent history, only return records linked to a dependent
      // Note: nil dependent_id is stored as "" via Ruby .to_s, so we must exclude empty string
      if (dependentOnly) {
        builder.addFilter({
          bool: {
            must_not: [{ term: { tas_program_instance_dependent_id_keyword: '' } }],
          },
        });
      }

      builder
        .addFilter({
          bool: {
            should: [
              { terms: { tas_application_state_keyword: ALL_APPLICATION_STATES } },
              { terms: { tas_program_instance_state_keyword: ALL_PROGRAM_INSTANCE_STATES } },
            ],
            minimum_should_match: 1,
          },
        })
        .setSort('updated_at:desc')
        .setPagination(1, 500); // Increased limit for history

      const query = {
        'model-type': 'TasProgramInstance',
        searchBody: builder.build(),
      };

      const response = (await this.api.call('/api/v2/search', {
        method: 'POST',
        body: JSON.stringify(query),
      })) as OpenSearchResponse<RecentApplication>;

      const responseBuilder = new OpenSearchResponseBuilder<RecentApplication>(response);
      const rawDocs = responseBuilder.getDocuments();
      const allApps = normalizePaymentFields(rawDocs);

      // Separate Type 4 and group by instance
      const type4InstanceMap = new Map<string, RecentApplication[]>();
      const flatApps: RecentApplication[] = [];
      const yearsSet = new Set<number>();

      for (const app of allApps) {
        // Extract year from updatedAt (matches displayed "last action" date)
        const dateStr = app.updatedAt || app.createdAt;
        if (dateStr) {
          const calendarYear = new Date(dateStr).getFullYear();
          // Include both the calendar year and the preceding year so academic-year
          // ranges (e.g. 2025-2026) are available when the last action falls in
          // the second calendar year of the academic period.
          yearsSet.add(calendarYear);
          yearsSet.add(calendarYear - 1);
        }

        const type = app.tasProgramTemplateTypeKeyword;
        const isInstanceRecord = app.recordType === 'instance';

        // Type 1: Program instance with no children - treat as instance
        if (type === 'TAS.ProgramType.1') {
          const instanceId = app.tasProgramInstanceIdKeyword || app.tasProgramInstanceId || app.id;
          if (!type4InstanceMap.has(instanceId)) {
            type4InstanceMap.set(instanceId, []);
          }
          type4InstanceMap.get(instanceId)!.push(app);
        }
        // Type 4.2/4.3 templates: Group instances with their applications
        else if (
          (type === 'TAS.ProgramType.4.2' || type === 'TAS.ProgramType.4.3') &&
          isInstanceRecord
        ) {
          // This is a Type 4 instance record
          const instanceId = app.tasProgramInstanceIdKeyword || app.tasProgramInstanceId || app.id;
          if (!type4InstanceMap.has(instanceId)) {
            type4InstanceMap.set(instanceId, []);
          }
          type4InstanceMap.get(instanceId)!.push(app);

          // OpenSearch may embed child application data in tasApplicationFields[] instead
          // of returning separate recordType='application' documents. Extract those as
          // synthetic child records so buildType4Instances can group them properly.
          const rawApp = app as unknown as Record<string, unknown>;
          const embeddedApps = rawApp['tasApplicationFields'] as
            | Record<string, unknown>[]
            | undefined;
          if (embeddedApps?.length) {
            for (const embedded of embeddedApps) {
              type4InstanceMap.get(instanceId)!.push({
                ...app,
                ...(embedded as Partial<RecentApplication>),
                recordType: 'application',
              });
            }
          }
        } else if (
          (type === 'TAS.ProgramType.4.2' || type === 'TAS.ProgramType.4.3') &&
          !isInstanceRecord
        ) {
          // This is a Type 4.2/4.3 application record
          const instanceId = app.tasProgramInstanceIdKeyword || app.tasProgramInstanceId || app.id;
          if (!type4InstanceMap.has(instanceId)) {
            type4InstanceMap.set(instanceId, []);
          }
          type4InstanceMap.get(instanceId)!.push(app);
        } else {
          // Type 2/3: Application data is nested in tasApplicationFields[] array,
          // not at the root level. Extract and merge application fields so the
          // component can render with correct ID, state, name, and payment data.
          const rawApp = app as unknown as Record<string, unknown>;
          const appFields = rawApp['tasApplicationFields'] as Record<string, unknown>[] | undefined;

          if (appFields?.length) {
            const firstApp = appFields[0];

            if (firstApp) {
              flatApps.push({
                ...app,
                ...(firstApp as Partial<RecentApplication>),
              });
            }
          } else if (app.tasApplicationIdKeyword) {
            // Real application document without embedded tasApplicationFields — keep as-is
            flatApps.push(app);
          }
          // Skip instance-only records with no applications (avoids phantom rows)
        }
      }

      // Deduplicate Type 4 child apps: if both a separate application document and an
      // embedded tasApplicationFields[] entry exist for the same application ID, keep
      // only the separate document (it has richer/more accurate data).
      for (const [mapKey, records] of type4InstanceMap) {
        // Find the actual instance document's id to distinguish embedded records
        const instanceRecord = records.find((r) => r.recordType === 'instance');
        const instanceDocId = instanceRecord?.id;

        // Only consider records with their own unique document id as "real"
        // application docs. Synthetic records created from embedded
        // tasApplicationFields[] share the instance document's id and should NOT count.
        const realAppIds = new Set(
          records
            .filter(
              (r) =>
                r.recordType !== 'instance' &&
                r.tasApplicationIdKeyword &&
                (!instanceDocId || r.id !== instanceDocId)
            )
            .map((r) => r.tasApplicationIdKeyword)
        );
        // Only deduplicate if there are real application docs whose IDs overlap with embedded ones
        if (realAppIds.size > 0) {
          const deduped = records.filter((r) => {
            // Keep all instance records
            if (r.recordType === 'instance') return true;
            // Keep real (non-embedded) application docs
            if (!instanceDocId || r.id !== instanceDocId) return true;
            // This is an embedded record — keep only if no real doc exists for this app ID
            return !realAppIds.has(r.tasApplicationIdKeyword);
          });
          type4InstanceMap.set(mapKey, deduped);
        }
      }

      // Build Type 1 and Type 4.2/4.3 instances with sorted child applications
      const programInstances = buildType4Instances(type4InstanceMap);
      const availableYears = Array.from(yearsSet);

      return { flatApplications: flatApps, programInstances, availableYears };
    } catch (error) {
      console.error('Failed to fetch benefit history:', error);
      return { flatApplications: [], programInstances: [], availableYears: [] };
    }
  }
}
